import React, { useEffect, useState } from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';

import api from 'api/api';
import { useApiRequests } from 'hooks';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { StaffItem } from 'types/api';
import { ConsentFormDefinition, ProviderInputField } from '../types';
import { getFormDefinition } from '../forms/dynamicRegistry';
import styles from './TeleconsentWizard.module.scss';

type Participant = {
  identity: string;
  displayName: string;
};

type Props = {
  // null = queue launch (no participant step); array = in-session launch
  participants: Participant[] | null;
  enabledFormKeys: string[];
  onBegin: (
    formKey: string,
    participantIdentity: string | null,
    providerPrefill?: Record<string, string>,
    participantDisplayName?: string,
    providerName?: string,
    clientLabel?: string,
  ) => void;
  onCancel: () => void;
};

type Step = 'participant' | 'form' | 'prefill';

const CLIENT_LABEL_OPTIONS = ['Default', 'Patient', 'Client', 'Other'];
const LS_CLIENT_LABEL_KEY = 'teleconsent_client_label';

const getDisplayName = (staff: { name: string; preferedDisplayName?: string | null }) =>
  staff.preferedDisplayName?.trim() || staff.name;

function readStoredLabel(): string {
  try {
    const stored = localStorage.getItem(LS_CLIENT_LABEL_KEY);
    if (stored && CLIENT_LABEL_OPTIONS.includes(stored)) return stored;
    if (stored) return 'Other';
  } catch {
    /* ignore */
  }
  return 'Default';
}

function readStoredCustomLabel(): string {
  try {
    const stored = localStorage.getItem(LS_CLIENT_LABEL_KEY);
    if (stored && !CLIENT_LABEL_OPTIONS.includes(stored)) return stored;
  } catch {
    /* ignore */
  }
  return '';
}

function writeStoredLabel(choice: string, custom: string) {
  try {
    const value = choice === 'Other' ? custom.trim() || 'Patient' : choice;
    localStorage.setItem(LS_CLIENT_LABEL_KEY, value);
  } catch {
    /* ignore */
  }
}

export function TeleconsentWizard({ participants, enabledFormKeys, onBegin, onCancel }: Props) {
  const { t } = useTranslation('teleconsent');
  const { getStaff } = useApiRequests();
  const currentUser = useAppSelector(selectUser);

  const [step, setStep] = useState<Step>(participants ? 'participant' : 'form');
  const [selectedParticipant, setSelectedParticipant] = useState<string | null>(null);
  const [selectedFormKey, setSelectedFormKey] = useState<string | null>(null);
  const [prefillValues, setPrefillValues] = useState<Record<string, string>>({});

  // Staff / provider selection — selectedProvider stores clinicUserId or '__custom'
  const [staffList, setStaffList] = useState<StaffItem[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [customProvider, setCustomProvider] = useState<string>('');

  // Client label — persisted in localStorage
  const [clientLabelChoice, setClientLabelChoice] = useState<string>(readStoredLabel);
  const [customClientLabel, setCustomClientLabel] = useState<string>(readStoredCustomLabel);

  const handleLabelChoiceChange = (val: string) => {
    setClientLabelChoice(val);
    writeStoredLabel(val, customClientLabel);
  };

  const handleCustomLabelChange = (val: string) => {
    setCustomClientLabel(val);
    writeStoredLabel(clientLabelChoice, val);
  };

  useEffect(() => {
    const currentId = currentUser?.clinicUser?.id?.toLowerCase().trim();

    Promise.allSettled([
      getStaff({ count: 200, offset: 0 }),
      api.request<StaffItem[]>({ url: '/api/invitation/admins', method: 'get' }),
    ]).then((results) => {
      const staffItems =
        results[0].status === 'fulfilled' ? (results[0].value as { items: StaffItem[] }).items ?? [] : [];
      const admins =
        results[1].status === 'fulfilled' && Array.isArray(results[1].value) ? (results[1].value as StaffItem[]) : [];

      const combined = [...staffItems, ...admins].reduce((acc: StaffItem[], user) => {
        if (user.clinicUserId && !acc.some((u) => u.clinicUserId === user.clinicUserId)) {
          acc.push(user);
        }
        return acc;
      }, []);

      // The backend always excludes the calling user from getStaff. Add them back here
      // so the logged-in provider can select themselves as the provider on the form.
      if (currentUser && currentId && !combined.some((s) => s.clinicUserId?.toLowerCase().trim() === currentId)) {
        const nameParts = [currentUser.title, currentUser.firstName, currentUser.lastName].filter(Boolean);
        const fullName =
          nameParts.join(' ') + (currentUser.clinicUser?.suffix ? `, ${currentUser.clinicUser.suffix}` : '');
        const me: StaffItem = {
          clinicUserId: currentUser.clinicUser.id,
          invitationId: '',
          name: fullName,
          preferedDisplayName: currentUser.clinicUser.preferedDisplayName ?? null,
          email: currentUser.email ?? '',
          status: 0,
          roomName: currentUser.clinicUser.roomName ?? '',
          hasLicense: currentUser.clinicUser.hasLicense,
          paymentsAllowed: currentUser.clinicUser.paymentsAllowed,
          speciality: currentUser.speciality ?? undefined,
        };
        combined.unshift(me);
      }

      setStaffList(combined);

      // Auto-select the current logged-in user
      if (currentId) {
        const me = combined.find((s) => s.clinicUserId?.toLowerCase().trim() === currentId);
        if (me) setSelectedProvider(me.clinicUserId);
      }
    });
  }, []);

  type FormEntry = { registryKey: string; def: ConsentFormDefinition };
  const _mapped = enabledFormKeys.map((k) => ({ registryKey: k, def: getFormDefinition(k) }));
  const availableForms = _mapped
    .filter((x): x is FormEntry => Boolean(x.def))
    .sort((a, b) => {
      const aCustom = a.def.isCustomForm ? 0 : 1;
      const bCustom = b.def.isCustomForm ? 0 : 1;
      if (aCustom !== bCustom) return aCustom - bCustom;
      return a.def.title.localeCompare(b.def.title);
    });
  const selectedForm = selectedFormKey ? getFormDefinition(selectedFormKey) ?? null : null;
  const prefillFields: ProviderInputField[] = selectedForm?.requiresProviderInput ?? [];

  const resolvedClientLabel = (): string => {
    if (clientLabelChoice === 'Other') return customClientLabel.trim() || 'Patient';
    if (clientLabelChoice === 'Default') return 'Patient';
    return clientLabelChoice;
  };

  const applyClientLabel = (text: string) => {
    const label = resolvedClientLabel();
    const lower = label.toLowerCase();
    const upper = label.toUpperCase();
    const title = label.charAt(0).toUpperCase() + label.slice(1);
    return text.replace(/\b(patient|client)\b/gi, (match) => {
      if (match === match.toUpperCase()) return upper;
      if (match[0] === match[0].toUpperCase()) return title;
      return lower;
    });
  };

  const requiredPrefillFilled = prefillFields
    .filter((f) => 'required' in f && f.required)
    .every((f) => (prefillValues[f.id] ?? '').trim().length > 0);

  const resolvedProviderName = (): string | undefined => {
    if (selectedProvider === '__custom') return customProvider.trim() || undefined;
    if (selectedProvider) {
      const staff = staffList.find((s) => s.clinicUserId === selectedProvider);
      if (staff) return getDisplayName(staff);
    }
    return undefined;
  };

  const resolvedProviderDetails = (): Record<string, string> => {
    const details: Record<string, string> = {};
    if (currentUser?.clinicUser?.organization) details['__organizationName'] = currentUser.clinicUser.organization;
    if (selectedProvider === '__custom') return details;

    const staff = staffList.find((s) => s.clinicUserId === selectedProvider);

    // Determine current user by direct ID comparison — independent of staff list entry format
    // so a preferred display name never interferes with individual field resolution.
    const isCurrentUser =
      !!currentUser &&
      !!selectedProvider &&
      selectedProvider.toLowerCase().trim() === currentUser.clinicUser.id.toLowerCase().trim();

    if (isCurrentUser && currentUser) {
      // Current user: always read from Redux store (most up-to-date, includes fields not in StaffItem)
      if (currentUser.firstName) details['__providerFirstName'] = currentUser.firstName;
      if (currentUser.lastName) details['__providerLastName'] = currentUser.lastName;
      if (currentUser.title) details['__providerTitle'] = currentUser.title;
      if (currentUser.clinicUser.suffix) details['__providerSuffix'] = currentUser.clinicUser.suffix;
      if (currentUser.speciality) details['__providerSpeciality'] = currentUser.speciality;
    } else if (staff) {
      // Other staff: use individual fields now returned by the backend
      if (staff.firstName) details['__providerFirstName'] = staff.firstName;
      if (staff.lastName) details['__providerLastName'] = staff.lastName;
      if (staff.title) details['__providerTitle'] = staff.title;
      if (staff.suffix) details['__providerSuffix'] = staff.suffix;
      if (staff.speciality) details['__providerSpeciality'] = staff.speciality;
    }

    if (!staff) return details;
    if (staff.email) details['__providerEmail'] = staff.email;
    if (staff.roomName) {
      const base = (process.env['REACT_APP_PUBLIC_URI'] ?? '').replace(/\/$/, '');
      details['__providerRoomURL'] = `${base}/${staff.roomName}`;
    }

    return details;
  };

  const handleBegin = () => {
    if (!selectedFormKey) return;
    const displayName = participants?.find((p) => p.identity === selectedParticipant)?.displayName;
    const details = resolvedProviderDetails();
    const basePrefill = prefillFields.length > 0 ? prefillValues : {};
    const merged = { ...basePrefill, ...details };
    onBegin(
      selectedFormKey,
      selectedParticipant,
      Object.keys(merged).length > 0 ? merged : undefined,
      displayName,
      resolvedProviderName(),
      resolvedClientLabel(),
    );
  };

  const handleFormSelect = (key: string) => {
    setSelectedFormKey(key);
    setPrefillValues({});
  };

  const handleFormNext = () => {
    if (!selectedFormKey) return;
    const form = getFormDefinition(selectedFormKey);
    if (form?.requiresProviderInput?.length) {
      setStep('prefill');
    } else {
      handleBegin();
    }
  };

  const setPrefill = (id: string, value: string) => {
    setPrefillValues((prev) => ({ ...prev, [id]: value }));
  };

  return (
    <div className={styles.scrim}>
      <div className={styles.card} onClick={(e) => e.stopPropagation()}>
        {/* PARTICIPANT STEP */}
        {step === 'participant' && participants && (
          <>
            <div className={styles.head}>
              <h2>{t('wizard.chooseParticipant')}</h2>
              <p>{t('wizard.chooseParticipantHint')}</p>
            </div>
            <div className={styles.list}>
              {participants.map((p) => (
                <button
                  key={p.identity}
                  className={`${styles.listItem} ${selectedParticipant === p.identity ? styles.selected : ''}`}
                  onClick={() => setSelectedParticipant(p.identity)}
                >
                  <span className={styles.avatar}>{p.displayName.slice(0, 2).toUpperCase()}</span>
                  <span>{p.displayName}</span>
                </button>
              ))}
            </div>
            <div className={styles.actions}>
              <button className={styles.btnSecondary} onClick={onCancel}>
                {t('wizard.cancel')}
              </button>
              <button className={styles.btnPrimary} disabled={!selectedParticipant} onClick={() => setStep('form')}>
                {t('wizard.next')}
              </button>
            </div>
          </>
        )}

        {/* FORM STEP */}
        {step === 'form' && (
          <>
            <div className={styles.head}>
              <h2>{t('wizard.chooseForm')}</h2>
              <p>{t('wizard.chooseFormHint')}</p>

              {/* Settings row: Provider + Client Label side-by-side */}
              <div className={styles.settingsRow}>
                <div className={styles.settingsField}>
                  <div className={styles.labelRow}>
                    <label className={styles.wizardLabel}>Form Provider</label>
                    <Tooltip title='Select which provider name and details should appear in the form.' arrow>
                      <InfoOutlinedIcon className={styles.wizardInfoIcon} />
                    </Tooltip>
                  </div>
                  <select
                    className={styles.wizardSelect}
                    value={selectedProvider}
                    onChange={(e) => setSelectedProvider(e.target.value)}
                  >
                    <option value=''>— None —</option>
                    {staffList.map((s) => (
                      <option key={s.clinicUserId} value={s.clinicUserId}>
                        {getDisplayName(s)}
                      </option>
                    ))}
                    <option value='__custom'>Custom…</option>
                  </select>
                  {selectedProvider === '__custom' && (
                    <input
                      className={styles.wizardInput}
                      type='text'
                      placeholder='Enter provider name'
                      value={customProvider}
                      onChange={(e) => setCustomProvider(e.target.value)}
                    />
                  )}
                </div>

                <div className={styles.settingsField}>
                  <div className={styles.labelRow}>
                    <label className={styles.wizardLabel}>Client Term</label>
                    <Tooltip title='Choose how the client will be referenced throughout the form.' arrow>
                      <InfoOutlinedIcon className={styles.wizardInfoIcon} />
                    </Tooltip>
                  </div>
                  <select
                    className={styles.wizardSelect}
                    value={clientLabelChoice}
                    onChange={(e) => handleLabelChoiceChange(e.target.value)}
                  >
                    {CLIENT_LABEL_OPTIONS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                  {clientLabelChoice === 'Other' && (
                    <input
                      className={styles.wizardInput}
                      type='text'
                      placeholder='Label…'
                      value={customClientLabel}
                      onChange={(e) => handleCustomLabelChange(e.target.value)}
                    />
                  )}
                </div>
              </div>
            </div>

            <div className={styles.list}>
              {availableForms.map(({ registryKey, def }) => (
                <button
                  key={registryKey}
                  className={`${styles.listItem} ${selectedFormKey === registryKey ? styles.selected : ''}`}
                  onClick={() => handleFormSelect(registryKey)}
                >
                  <span className={`${styles.formIcon} ${def.isCustomForm ? styles.formIconCustom : ''}`}>
                    {def.isCustomForm ? (
                      <LayersOutlinedIcon style={{ fontSize: 16 }} />
                    ) : (
                      <LockOutlinedIcon style={{ fontSize: 16 }} />
                    )}
                  </span>
                  <span className={styles.formLabel}>
                    <span className={styles.formTitle}>{def.title}</span>
                    {def.subtitle && <span className={styles.formSubtitle}>{def.subtitle}</span>}
                  </span>
                  {def.requiresProviderInput?.length ? (
                    <span className={styles.prefillBadge}>{t('wizard.prefillRequired')}</span>
                  ) : null}
                </button>
              ))}
            </div>

            <div className={`${styles.actions} ${styles.actionsEnd}`}>
              {participants ? (
                <button className={styles.btnSecondary} onClick={() => setStep('participant')}>
                  {t('wizard.back')}
                </button>
              ) : (
                <button className={styles.btnSecondary} onClick={onCancel}>
                  {t('wizard.cancel')}
                </button>
              )}
              <button className={styles.btnPrimary} disabled={!selectedFormKey} onClick={handleFormNext}>
                {selectedForm?.requiresProviderInput?.length ? t('wizard.next') : t('wizard.begin')}
              </button>
            </div>
          </>
        )}

        {/* PROVIDER PRE-FILL STEP */}
        {step === 'prefill' && selectedForm && (
          <>
            <div className={styles.head}>
              <h2>{t('wizard.providerPrefill')}</h2>
              <p>{t('wizard.providerPrefillHint', { title: selectedForm.title })}</p>
            </div>
            <div className={styles.prefillForm}>
              {(() => {
                const rows: ProviderInputField[][] = [];
                let i = 0;
                while (i < prefillFields.length) {
                  const f = prefillFields[i];
                  const next = prefillFields[i + 1];
                  if ('halfWidth' in f && f.halfWidth && next && 'halfWidth' in next && next.halfWidth) {
                    rows.push([f, next]);
                    i += 2;
                  } else {
                    rows.push([f]);
                    i++;
                  }
                }
                return rows.map((row, ri) =>
                  row.length === 2 ? (
                    <div key={ri} className={styles.prefillRow}>
                      {row.map((f) => (
                        <div key={f.id} className={styles.prefillField}>
                          <label>
                            {applyClientLabel(f.label)}
                            {'required' in f && f.required && <span className={styles.req}>*</span>}
                          </label>
                          <input
                            type={f.type}
                            value={prefillValues[f.id] ?? ''}
                            onChange={(e) => setPrefill(f.id, e.target.value)}
                            placeholder={'placeholder' in f ? f.placeholder : applyClientLabel(f.label)}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div key={ri} className={styles.prefillField}>
                      <label>
                        {applyClientLabel(row[0].label)}
                        {'required' in row[0] && row[0].required && <span className={styles.req}>*</span>}
                      </label>
                      {row[0].type === 'textarea' ? (
                        <textarea
                          rows={row[0].rows ?? 3}
                          value={prefillValues[row[0].id] ?? ''}
                          onChange={(e) => setPrefill(row[0].id, e.target.value)}
                          placeholder={applyClientLabel(row[0].label)}
                        />
                      ) : (
                        <input
                          type={row[0].type}
                          value={prefillValues[row[0].id] ?? ''}
                          onChange={(e) => setPrefill(row[0].id, e.target.value)}
                          placeholder={'placeholder' in row[0] ? row[0].placeholder : applyClientLabel(row[0].label)}
                        />
                      )}
                    </div>
                  ),
                );
              })()}
            </div>
            <div className={`${styles.actions} ${styles.actionsEnd}`}>
              <button className={styles.btnSecondary} onClick={() => setStep('form')}>
                {t('wizard.back')}
              </button>
              <button className={styles.btnPrimary} disabled={!requiredPrefillFilled} onClick={handleBegin}>
                {t('wizard.begin')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
